import React, { useEffect, useState } from 'react';

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  background: '#fff',
  padding: '20px',
  borderRadius: '8px',
  maxWidth: '600px',
  width: '90%',
  maxHeight: '90%',
  overflowY: 'auto',
};

export const getLaunchPredictionBadgeLabel = prediction => (
  prediction && prediction.successPercent != null ? `${prediction.successPercent}%` : '--'
);

const WEATHER_OVERRIDE_OPTIONS = [
  [1, 'No Override'],
  [0.5, 'Reduce 50%'],
  [0, 'Waive Weather'],
];

const formatWeatherOverrideFactor = value => {
  const factor = Number(value ?? 1);
  if (factor === 0) return '100% weather credit';
  if (factor === 0.5) return '50% weather credit';
  return 'No override';
};

export default function LaunchSuccessAdvisorModal({
  prediction,
  feedback,
  onFeedbackChange,
  onSaveFeedback,
  aiAssessment,
  isAiAssessmentLoading,
  aiAssessmentError,
  onRunAiAssessment,
  canManageWeatherOverride = false,
  isWeatherOverrideSaving = false,
  onWeatherOverrideChange,
  onClose,
}) {
  const [weatherOverrideReason, setWeatherOverrideReason] = useState('');

  useEffect(() => {
    setWeatherOverrideReason(prediction?.weatherOverrideReason || prediction?.factors?.weatherImpact?.overrideReason || '');
  }, [prediction?.id, prediction?.weatherOverrideReason, prediction?.factors?.weatherImpact?.overrideReason]);

  if (!prediction) return null;
  const openIssueCount = Number(prediction.factors?.openIssueCount || 0);
  const redBallCount = Number(prediction.factors?.redBallCount || 0);
  const weatherImpact = prediction.factors?.weatherImpact || {};
  const weatherOverrideFactor = Number(prediction.weatherOverrideFactor ?? weatherImpact.overrideFactor ?? 1);
  const originalWeatherPenalty = Number(weatherImpact.originalPenaltyPoints ?? weatherImpact.penaltyPoints ?? 0);
  const originalWeatherDelay = Number(weatherImpact.originalDelayMinutes ?? weatherImpact.delayMinutes ?? 0);
  const originalWeatherCap = Number(weatherImpact.originalMaxSuccessPercent ?? weatherImpact.maxSuccessPercent ?? 100);
  const hasWeatherImpactData = Boolean(weatherImpact?.status && weatherImpact.status !== 'not_used');
  const hasWeatherPenalty = originalWeatherPenalty > 0 || originalWeatherDelay > 0 || originalWeatherCap < 100;
  const openIssueDetail = [
    `${openIssueCount} open`,
    prediction.factors?.category ? `Category: ${prediction.factors.category}` : '',
    prediction.factors?.shop ? `Shop: ${prediction.factors.shop}` : '',
  ].filter(Boolean).join(' | ');

  const updateFeedback = patch => {
    if (onFeedbackChange) {
      onFeedbackChange({
        accepted: feedback?.accepted || '',
        feedbackAction: feedback?.feedbackAction || '',
        feedbackReason: feedback?.feedbackReason || '',
        ...patch,
      });
    }
  };

  const applyWeatherOverride = factor => {
    if (!onWeatherOverrideChange || !canManageWeatherOverride || isWeatherOverrideSaving) return;
    onWeatherOverrideChange(factor, weatherOverrideReason);
  };

  const weatherOverridePanel = hasWeatherImpactData ? (
    <div className="launch-success-weather-override">
      <div className="launch-success-weather-override__header">
        <div>
          <h4>Weather Override</h4>
          <p>
            Production may reduce the weather penalty when local ramp conditions support it.
          </p>
        </div>
        <span>{formatWeatherOverrideFactor(weatherOverrideFactor)}</span>
      </div>
      <div className="launch-success-weather-override__metrics">
        <span>Original: {originalWeatherPenalty} pts / +{originalWeatherDelay} min / {originalWeatherCap}% cap</span>
        <span>Applied: {weatherImpact.penaltyPoints || 0} pts / +{weatherImpact.delayMinutes || 0} min / {weatherImpact.maxSuccessPercent || 100}% cap</span>
      </div>
      <div className="launch-success-weather-override__controls">
        {WEATHER_OVERRIDE_OPTIONS.map(([factor, label]) => (
          <button
            key={factor}
            type="button"
            className={Number(weatherOverrideFactor) === Number(factor) ? 'is-active' : ''}
            onClick={() => applyWeatherOverride(factor)}
            disabled={!canManageWeatherOverride || isWeatherOverrideSaving || !hasWeatherPenalty}
          >
            {label}
          </button>
        ))}
        <input
          type="text"
          value={weatherOverrideReason}
          onChange={event => setWeatherOverrideReason(event.target.value)}
          placeholder="Observed weather note"
          disabled={!canManageWeatherOverride || isWeatherOverrideSaving}
        />
      </div>
      {!canManageWeatherOverride ? (
        <small>Only Production, Management, Pro-Super, Expediter, siteAdmin, or superuser accounts can apply this override.</small>
      ) : !hasWeatherPenalty ? (
        <small>No active weather penalty is applied to this launch line.</small>
      ) : weatherImpact.overrideApplied ? (
        <small>
          Override applied by {weatherImpact.overrideBy || prediction.weatherOverrideBy || '--'}
          {weatherImpact.overrideAt || prediction.weatherOverrideAt ? ` at ${weatherImpact.overrideAt || prediction.weatherOverrideAt}` : ''}.
        </small>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="modal-overlay">
      <div style={modalOverlayStyle}>
        <div style={modalContentStyle} className="launch-success-modal">
          <button
            type="button"
            className="launch-success-modal__close"
            aria-label="Close Launch Success Advisor"
            onClick={onClose}
          >
            X
          </button>
          <div className="launch-success-modal__header">
            <div>
              <h3>Launch Success Advisor</h3>
              <p>Tail {prediction.tail || '--'} | Line {prediction.row?.lineNum || '--'}</p>
            </div>
            <span className={`launch-success-modal__percent is-${prediction.successBand || 'insufficient'}`}>
              {getLaunchPredictionBadgeLabel(prediction)}
            </span>
          </div>
          <div className="launch-success-modal__recommendation">
            <strong>Chance to Make Launch:</strong> {getLaunchPredictionBadgeLabel(prediction)}
            <span>{prediction.recommendation}</span>
          </div>
          <div className="launch-success-modal__grid">
            <section>
              <h4>Factors Used</h4>
              <dl className="launch-success-factor-list">
                <div className={openIssueCount > 0 ? 'is-warning' : ''}>
                  <dt>Open Issues</dt>
                  <dd>{openIssueCount > 0 ? openIssueDetail : (prediction.factors?.openIssueCount ?? '--')}</dd>
                </div>
                <div className={redBallCount > 0 ? 'is-critical' : ''}><dt>Red Balls</dt><dd>{prediction.factors?.redBallCount ?? '--'}</dd></div>
                <div><dt>Time Remaining</dt><dd>{prediction.factors?.timeRemainingMinutes ?? '--'} min</dd></div>
                <div><dt>Category</dt><dd>{prediction.factors?.category || '--'}</dd></div>
                <div><dt>Shop</dt><dd>{prediction.factors?.shop || '--'}</dd></div>
                <div><dt>Historical Jobs</dt><dd>{prediction.factors?.historicalSampleSize ?? 0}</dd></div>
                <div><dt>Median / P75 / P90</dt><dd>{prediction.factors?.medianRepairMinutes ?? '--'} / {prediction.factors?.p75RepairMinutes ?? '--'} / {prediction.factors?.p90RepairMinutes ?? '--'} min</dd></div>
                <div><dt>Qualified Personnel</dt><dd>{prediction.factors?.qualifiedPersonnel ?? '--'} of {prediction.factors?.presentShopPersonnel ?? '--'} present</dd></div>
                <div><dt>Available Spares</dt><dd>{prediction.factors?.availableSpares ?? 0}</dd></div>
                <div><dt>Launch Flow Penalty</dt><dd>{prediction.factors?.launchFlow?.totalPenalty ?? 0} pts</dd></div>
                {weatherImpact?.status && weatherImpact.status !== 'not_used' ? (
                  <div className={weatherImpact.penaltyPoints > 0 ? (weatherImpact.penaltyPoints >= 18 ? 'is-critical' : 'is-warning') : ''}>
                    <dt>Weather Impact</dt>
                    <dd>
                      {weatherImpact.penaltyPoints || 0} pts
                      {weatherImpact.delayMinutes ? `; +${weatherImpact.delayMinutes} min` : ''}
                      {weatherImpact.risk && weatherImpact.risk !== 'unknown' ? `; ${weatherImpact.risk}` : ''}
                    </dd>
                  </div>
                ) : null}
                {weatherImpact?.maxSuccessPercent < 100 ? (
                  <div className="is-warning">
                    <dt>Weather Risk Cap</dt>
                    <dd>{weatherImpact.maxSuccessPercent}% maximum</dd>
                  </div>
                ) : null}
                {(weatherImpact?.reasons || []).length > 0 ? (
                  <div className={`${weatherImpact.penaltyPoints > 0 ? 'is-warning ' : ''}is-weather-reasons`}>
                    <dt>Weather Reasons</dt>
                    <dd>
                      {(weatherImpact.reasons || []).slice(0, 3).map(reason => (
                        <span key={reason} className="launch-success-milestone-chip">
                          {reason}
                        </span>
                      ))}
                    </dd>
                  </div>
                ) : null}
                {prediction.factors?.launchFlow?.maxSuccessPercent < 100 ? (
                  <div>
                    <dt>Launch Flow Risk Cap</dt>
                    <dd>
                      {prediction.factors.launchFlow.maxSuccessPercent}% maximum
                      {prediction.factors.launchFlow.riskCapReason
                        ? `: ${prediction.factors.launchFlow.riskCapReason}`
                        : ''}
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>Minimum Flow Time</dt>
                  <dd>
                    {prediction.factors?.launchFlow?.baselineNeededMinutes ?? 0} min needed;{' '}
                    {prediction.factors?.launchFlow?.baselineShortfallMinutes ?? 0} min short
                    {prediction.factors?.launchFlow?.baselineSlackMinutes !== null && prediction.factors?.launchFlow?.baselineSlackMinutes !== undefined
                      ? `; ${prediction.factors.launchFlow.baselineSlackMinutes} min slack`
                      : ''}
                  </dd>
                </div>
                <div>
                  <dt>Milestone Timing</dt>
                  <dd>
                    {(prediction.factors?.launchFlow?.milestones || []).map(item => (
                      <span key={item.key} className="launch-success-milestone-chip">
                        {item.label}: {item.lateMinutes > 0 ? `${item.lateMinutes}m late` : 'OK'}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>
            </section>
            {weatherOverridePanel ? (
              <div className="launch-success-weather-override-slot launch-success-weather-override-slot--mobile">
                {weatherOverridePanel}
              </div>
            ) : null}
            <section>
              <h4>Similar Prior Jobs</h4>
              {(prediction.similarJobs || []).length > 0 ? (
                <div className="reports-table-shell">
                  <table className="reports-table launch-success-jobs">
                    <thead>
                      <tr>
                        <th>Tail</th>
                        <th>Shop</th>
                        <th>Duration</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prediction.similarJobs.map(job => (
                        <tr key={job.id}>
                          <td>{job.tail || '--'}</td>
                          <td>{job.shop || '--'}</td>
                          <td>{job.durationMinutes} min</td>
                          <td>{job.description || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="reports-empty">No similar closed jobs found yet. This prediction needs more history.</p>
              )}
            </section>
          </div>
          {weatherOverridePanel ? (
            <div className="launch-success-weather-override-slot launch-success-weather-override-slot--desktop">
              {weatherOverridePanel}
            </div>
          ) : null}
          {onRunAiAssessment ? (
            <section className="launch-success-ai-assessment">
              <div className="launch-success-ai-assessment__header">
                <div>
                  <h4>What's Next?</h4>
                  <p>
                    Optional AI advice using this flight, surrounding 24-hour priorities, parts,
                    repair history, spares, and personnel availability.
                  </p>
                </div>
                <button type="button" onClick={onRunAiAssessment} disabled={isAiAssessmentLoading}>
                  {isAiAssessmentLoading ? 'Assessing...' : aiAssessment ? 'Run Again' : "What's Next?"}
                </button>
              </div>
              {aiAssessmentError ? (
                <div className="launch-success-ai-assessment__error">{aiAssessmentError}</div>
              ) : null}
              {isAiAssessmentLoading ? (
                <div className="launch-success-ai-assessment__loading">
                  Reviewing the flight and its effect on the 24-hour plan...
                </div>
              ) : null}
              {aiAssessment && !isAiAssessmentLoading ? (
                <div className="launch-success-ai-assessment__result">
                  <div>
                    <span>Recommended Next Action</span>
                    <strong>{aiAssessment.recommendedNextAction || 'Continue monitoring the launch plan.'}</strong>
                    {aiAssessment.executeBy ? <small>Timing: {aiAssessment.executeBy}</small> : null}
                  </div>
                  <p>{aiAssessment.why || aiAssessment.assessment}</p>
                  {aiAssessment.decisionPoint ? (
                    <div className="launch-success-ai-assessment__decision">
                      <strong>Decision point</strong>
                      <span>{aiAssessment.decisionPoint}</span>
                    </div>
                  ) : null}
                  {aiAssessment.risks?.length ? (
                    <div>
                      <strong>Risks</strong>
                      <ul>
                        {aiAssessment.risks.map((risk, index) => <li key={`${risk}-${index}`}>{risk}</li>)}
                      </ul>
                    </div>
                  ) : null}
                  <small>
                    Confidence: {aiAssessment.confidence || '--'} | Advisory only; no action was executed.
                  </small>
                </div>
              ) : null}
            </section>
          ) : null}
          {prediction.id && onFeedbackChange ? (
            <section className="launch-success-feedback">
              <h4>Prediction Feedback</h4>
              <div className="launch-success-feedback__controls">
                <select
                  value={feedback?.accepted || ''}
                  onChange={e => updateFeedback({ accepted: e.target.value })}
                >
                  <option value="">Accept or reject recommendation</option>
                  <option value="true">Accepted</option>
                  <option value="false">Rejected</option>
                </select>
                <select
                  value={feedback?.feedbackAction || ''}
                  onChange={e => updateFeedback({ feedbackAction: e.target.value })}
                >
                  <option value="">Action taken</option>
                  <option value="keep_original_tail">Keep original tail</option>
                  <option value="prep_spare">Prep spare</option>
                  <option value="swap_spare">Swap to spare</option>
                  <option value="coordinate_slip">Coordinate slip</option>
                  <option value="cancel_sortie">Cancel sortie</option>
                  <option value="reassign_personnel">Reassign personnel</option>
                </select>
                <input
                  type="text"
                  value={feedback?.feedbackReason || ''}
                  onChange={e => updateFeedback({ feedbackReason: e.target.value })}
                  placeholder="Reason / production notes"
                />
                <button type="button" onClick={onSaveFeedback}>Save Feedback</button>
              </div>
            </section>
          ) : null}
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <button type="button" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
